// @flow
import React, { Component, Fragment } from 'react';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Mobile, Default } from '../../../utils/responsive';
import { Base64 } from 'js-base64';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import AlertContainer from '../../alert/container/AlertContainer';
import InfiniteScroll from '../../../elements/InfiniteScroll/InfiniteScroll';
import Loading from '../../../elements/Loading/Loading';
import environment from '../../../environment';
import moment from 'moment';
import { CommaFormat } from '../../../elements/Price/Price';

import style from './BestDeals.scss';
import icons from '../../../assets/styles/icons.scss';
import buttonStyle from '../../../assets/styles/button.scss';
import headerImageDesktopEs from '../../../assets/img/best-deals/Banner-spa-1.png';
import headerImageMobileEs from '../../../assets/img/best-deals/Banner-spa-2.png';
import headerImageDesktopEn from '../../../assets/img/best-deals/Banner-eng-1.png';
import headerImageMobileEn from '../../../assets/img/best-deals/Banner-eng-2.png';

import type { Deal } from '../duck';
import type { LocalValuesState } from '../../local-values/duck';
import type { HTTPError } from '../../../error';
import type { LocaleState } from '../../locale/duck';
import BlackFridayHeader from './BlackFridayHeader/BlackFridayHeader';

type Props = {
  +deals?: Deal[];
  +isFetchingDeals?: boolean;
  +error?: HTTPError;
  +dealsPage: number;
  +dealsPageCanAsk?: boolean;
  +localValues: LocalValuesState;
  +fetchDeals: () => void;
  +resetDealsState: () => void;
  +resetAlertState: () => void;
  +locale: LocaleState;
};

const messages = defineMessages({
  title: {
    id: 'bestDeals.document.title',
  },
  description: {
    id: 'bestDeals.document.description',
  },
  day: {
    id: 'bestDeals.expires.day',
  },
  days: {
    id: 'bestDeals.expires.days',
  },
});

const headerImage = {
  en: {
    mobile: headerImageMobileEn,
    desktop: headerImageDesktopEn,
  },
  es: {
    mobile: headerImageMobileEs,
    desktop: headerImageDesktopEs,
  },
};

export default class BestDeals extends Component<Props, void> {

  componentDidMount() {
    const { fetchDeals, deals } = this.props;
    if (!deals) {
      fetchDeals();
    }
  }

  componentWillUnmount() {
    const { resetDealsState, resetAlertState } = this.props;
    resetDealsState();
    resetAlertState();
  }

  buildUrl = (item: Deal): string => {
    const i: { [key: string]: string | number | boolean } = {
      url: item.url,
      name: item.name,
      detail: item.detail,
      price: item.priceNow,
      weight: item.weight,
      originalPackaging: item.originalPackaging,
      category: item.categoryId,
      amount: item.amount,
      image: item.image,
    };
    if (!!item.volumeTier) {
      i.volumeTier = item.volumeTier.code;
    }
    const encode = Base64.encode(JSON.stringify(i));
    return `/new-order/product?product=${encode}`;
  };

  buildItems() {
    const { deals } = this.props;
    return deals && deals.map((item: Deal, index: number) => {
      const moneySaved = parseFloat(item.priceBefore) - parseFloat(item.priceNow);
      // Note: We add a day because the deal is valid on the expiration day.
      const timeToExpires = moment(item.expires).add(2, 'days').diff(moment(), 'days');
      const timeLabel = `${timeToExpires}  ${this.context.intl.formatMessage(timeToExpires === 1 ? messages.day : messages.days)}`;
      const url = this.buildUrl(item);
      return (
        <article className={style.dealBox}
                 key={index}>
          <p className={style.name}>{item.name}</p>
          <FormattedMessage id="bestDeals.item.provider">{
            text => <p className={style.provider}>{`${text} ${item.provider}`}</p>
          }</FormattedMessage>
          <section className={style.content}>
            <section className={style.product}>
              <img src={item.image} alt="Qempo"/>
              <section className={style.detail}>
                <h2 className={style.price}>$ <CommaFormat price={item.priceNow}/></h2>
                <FormattedMessage id="bestDeals.item.before">{
                  text => <p className={style.before}>{text} $ <CommaFormat price={item.priceBefore}/></p>
                }</FormattedMessage>
                <FormattedMessage id="bestDeals.item.saved">{
                  text => <p className={style.saved}>
                    {`${text} $ ${!isNaN(moneySaved) ? CommaFormat({ price: moneySaved.toFixed(2) }) : '0.00'}`}
                  </p>
                }</FormattedMessage>
                <FormattedMessage id="bestDeals.item.priceFrom">{
                  text => <p className={style.priceFrom}>{text}</p>
                }</FormattedMessage>
                <FormattedMessage id={timeToExpires === 1 ? 'bestDeals.item.lastDay' : 'bestDeals.item.finished'}>{
                  text => <p className={classNames(style.expires, { [style.red]: timeToExpires === 1 })}>
                    <i className={classNames(icons.iconTimerMedium,
                      { [style.red]: timeToExpires === 1 })}/>{timeToExpires === 1 ? text : `${text} ${timeLabel}`}
                  </p>
                }</FormattedMessage>
              </section>
            </section>
            <section className={style.buttons}>
              <FormattedMessage id="bestDeals.item.button.quote">{
                text => <Link to={url}
                              className={classNames(style.quote, buttonStyle.btn, buttonStyle.lightBlueInverse)}>{text}</Link>
              }</FormattedMessage>
              {/*<FormattedMessage id="bestDeals.item.button.add">{
                text => <Link to={url}
                              className={classNames(style.addToCart, buttonStyle.btn, buttonStyle.orangeInverse)}>
                  <i className={icons.iconShoppingCartMedium}/>{text}</Link>
              }</FormattedMessage>*/}
            </section>
          </section>
        </article>);
    });
  }

  render() {
    const { isFetchingDeals, dealsPageCanAsk, fetchDeals, locale } = this.props;
    const description = this.context.intl.formatMessage(messages.description);
    const title = this.context.intl.formatMessage(messages.title);
    const baseUrl = environment.baseUrl[locale.domainCountry || 'US'];
    const coverImg = headerImage[locale.locale].mobile;
    return (
      <Fragment>
        <Helmet meta={[
          { name: 'description', content: description },
          { property: 'og:image', content: `${baseUrl}${coverImg}` },
          { property: 'og:title', content: title },
          { property: 'og:description', content: description },
          { name: 'twitter:title', content: title },
          { name: 'twitter:twitter:image', content: `${baseUrl}${coverImg}` },
          { name: 'twitter:twitter:description', content: description },
        ]}>
          <title>{title}</title>
        </Helmet>
        {/*<BlackFridayHeader/>*/}
        <header className={style.bestDealsHeader}>
          <section className={style.alertBar}>
            <AlertContainer showId="best-deals"/>
          </section>
          <section className={style.headerImage}>
            <Default>
              <img src={headerImage[locale.locale].desktop} alt="Qempo"/>
            </Default>
            <Mobile>
              <img src={headerImage[locale.locale].mobile} alt="Qempo"/>
            </Mobile>
          </section>
          <FormattedMessage id="bestDeals.title">{
            text => <h2>{text}</h2>
          }</FormattedMessage>
        </header>
        <InfiniteScroll offset={350}
                         disabled={isFetchingDeals || !dealsPageCanAsk}
                         handleCheckpoint={() => fetchDeals()}>
          <section className={style.itemsWrap}>
            {isFetchingDeals && <Loading className={style.loadingDeals}/>}
            {this.buildItems()}
          </section>
        </InfiniteScroll>
      </Fragment>);
  }
}

BestDeals.propTypes = {
  deals: PropTypes.array,
  isFetchingDeals: PropTypes.bool,
  error: PropTypes.object,
  dealsPage: PropTypes.number,
  dealsPageCanAsk: PropTypes.bool,
  localValues: PropTypes.object.isRequired,
  fetchDeals: PropTypes.func,
  resetDealsState: PropTypes.func.isRequired,
  resetAlertState: PropTypes.func.isRequired,
  locale: PropTypes.object.isRequired,
};

BestDeals.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/best-deals/components/BestDeals.jsx